/**
 * Camera capture helpers.
 *
 * The booth sends the guest's photo as a base64 data URL, so everything here
 * ends at a JPEG string the API can accept directly.
 */

export interface CaptureOptions {
  /** Mirror the frame — a selfie camera preview is mirrored, so the capture
      must be too, or the guest gets a photo that doesn't match what they saw. */
  mirrored?: boolean;
  maxDimension?: number;
  quality?: number;
}

export function captureFromVideo(video: HTMLVideoElement, options: CaptureOptions = {}): string {
  const { mirrored = true, maxDimension = 1600, quality = 0.92 } = options;

  const sourceWidth = video.videoWidth;
  const sourceHeight = video.videoHeight;
  if (!sourceWidth || !sourceHeight) throw new Error("Camera is not ready yet");

  const scale = Math.min(1, maxDimension / Math.max(sourceWidth, sourceHeight));
  const canvas = document.createElement("canvas");
  canvas.width = Math.round(sourceWidth * scale);
  canvas.height = Math.round(sourceHeight * scale);

  const context = canvas.getContext("2d");
  if (!context) throw new Error("Could not get a canvas context");

  if (mirrored) {
    context.translate(canvas.width, 0);
    context.scale(-1, 1);
  }
  context.drawImage(video, 0, 0, canvas.width, canvas.height);

  return canvas.toDataURL("image/jpeg", quality);
}

export function fileToDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(new Error("Could not read that file"));
    reader.readAsDataURL(file);
  });
}

/** Crops to an aspect ratio, centred — used to match the frame's face cutout. */
export function cropToAspect(dataUrl: string, aspect: number): Promise<string> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => {
      const sourceAspect = image.width / image.height;
      let sx = 0, sy = 0, sw = image.width, sh = image.height;

      if (sourceAspect > aspect) {
        sw = image.height * aspect;
        sx = (image.width - sw) / 2;
      } else {
        sh = image.width / aspect;
        sy = (image.height - sh) / 2;
      }

      const canvas = document.createElement("canvas");
      canvas.width = Math.round(sw);
      canvas.height = Math.round(sh);
      const context = canvas.getContext("2d");
      if (!context) return reject(new Error("Could not get a canvas context"));

      context.drawImage(image, sx, sy, sw, sh, 0, 0, canvas.width, canvas.height);
      resolve(canvas.toDataURL("image/jpeg", 0.92));
    };
    image.onerror = () => reject(new Error("Could not load that image"));
    image.src = dataUrl;
  });
}
